import React, { useRef, useState } from "react";
import { X, Loader2, ImagePlus, Video as VideoIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import { VideoEmbed } from "@/components/common/VideoEmbed";
import { VideoField, uploadFile } from "@/components/forms/MediaUploader";
import { validateUpload } from "@/lib/uploadLimits";

export const EMPTY_MEDIA = { images: [], videos: [] };

/** Coerce legacy/loose media shapes into { images, videos }. */
function normalize(media) {
  if (!media) return { images: [], videos: [] };
  if (media.url && !media.images && !media.videos) {
    if (media.type === "image") return { images: [media.url], videos: [] };
    return { images: [], videos: [media] };
  }
  return { images: media.images || [], videos: media.videos || [] };
}

export function hasMedia(media) {
  const m = normalize(media);
  return m.images.length > 0 || m.videos.length > 0;
}

/** Read-only render of a post's media: photo grid (with +N + lightbox) and up to 2 videos. */
export function PostMediaView({ media }) {
  const { images, videos } = normalize(media);
  const [lightbox, setLightbox] = useState(-1);
  if (images.length === 0 && videos.length === 0) return null;
  const shown = images.slice(0, 4);
  const extra = images.length - shown.length;
  return (
    <div className="mt-3 space-y-3">
      {images.length > 0 && (
        <div className={`grid gap-1.5 ${shown.length === 1 ? "grid-cols-1" : "grid-cols-2"}`} data-testid="post-image-grid">
          {shown.map((url, i) => (
            <button key={i} type="button" onClick={() => setLightbox(i)} className="relative overflow-hidden bg-secondary" data-testid={`post-image-${i}`}>
              <img src={fileUrl(url)} alt="" className={`w-full object-cover ${shown.length === 1 ? "max-h-96" : "aspect-square"}`} />
              {i === 3 && extra > 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/55 text-xl font-[680] text-white" data-testid="post-image-more">+{extra}</div>
              )}
            </button>
          ))}
        </div>
      )}
      {videos.map((v, i) => <VideoEmbed key={i} video={v} />)}

      <Dialog open={lightbox >= 0} onOpenChange={(o) => !o && setLightbox(-1)}>
        <DialogContent className="max-w-3xl border-0 bg-transparent p-0 shadow-none" data-testid="post-image-lightbox">
          {lightbox >= 0 && (
            <div className="relative flex items-center justify-center">
              <img src={fileUrl(images[lightbox])} alt="" className="max-h-[82vh] w-full object-contain" />
              {images.length > 1 && (
                <>
                  <button onClick={() => setLightbox((lightbox - 1 + images.length) % images.length)} aria-label="Previous" className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/55 p-2 text-white hover:bg-black/70"><ChevronLeft className="h-5 w-5" /></button>
                  <button onClick={() => setLightbox((lightbox + 1) % images.length)} aria-label="Next" className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/55 p-2 text-white hover:bg-black/70"><ChevronRight className="h-5 w-5" /></button>
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/55 px-2 py-0.5 text-xs text-white">{lightbox + 1} / {images.length}</div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Composer attach UI: multiple photos (grid) + up to 2 videos (YouTube or upload). */
export function ChatterComposerMedia({ value, onChange, compact = false }) {
  const images = value.images || [];
  const videos = value.videos || [];
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [draft, setDraft] = useState({ type: "youtube", url: "" });

  const onPick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    const bad = files.find((f) => !validateUpload(f, "image").ok);
    if (bad) { toast.error(validateUpload(bad, "image").message); return; }
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadFile(f, "image"));
      onChange({ ...value, images: [...images, ...urls] });
    } catch { toast.error("Image upload failed."); }
    finally { setUploading(false); }
  };
  const addVideo = () => {
    if (!draft.url) return;
    if (videos.length >= 2) { toast.error("Up to 2 videos per post."); return; }
    onChange({ ...value, videos: [...videos, draft] });
    setDraft({ type: "youtube", url: "" });
    setShowVideo(false);
  };

  return (
    <div className={compact ? "" : "mt-2"} data-testid="chatter-composer-media">
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} />
      <div className="flex flex-wrap gap-1">
        <Button type="button" variant="ghost" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="chatter-add-photo">
          {uploading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-1.5 h-4 w-4" />} Photo
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setShowVideo((s) => !s)} disabled={videos.length >= 2} data-testid="chatter-add-video">
          <VideoIcon className="mr-1.5 h-4 w-4" /> Video
        </Button>
      </div>

      {images.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {images.map((url, i) => (
            <div key={i} className="relative h-16 w-16 overflow-hidden border border-border">
              <img src={fileUrl(url)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => onChange({ ...value, images: images.filter((_, idx) => idx !== i) })} className="absolute right-0 top-0 bg-black/60 p-0.5 text-white" aria-label="Remove photo" data-testid={`chatter-remove-photo-${i}`}><X className="h-3 w-3" /></button>
            </div>
          ))}
        </div>
      )}
      {videos.length > 0 && (
        <div className="mt-2 space-y-2">
          {videos.map((v, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="min-w-0 flex-1"><VideoEmbed video={v} /></div>
              <Button type="button" variant="secondary" size="sm" onClick={() => onChange({ ...value, videos: videos.filter((_, idx) => idx !== i) })} data-testid={`chatter-remove-video-${i}`}>Remove</Button>
            </div>
          ))}
        </div>
      )}
      {showVideo && videos.length < 2 && (
        <div className="mt-2 border border-border p-3">
          <VideoField value={draft} onChange={setDraft} testId="chatter-video-field" />
          <div className="mt-2 flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowVideo(false); setDraft({ type: "youtube", url: "" }); }}>Cancel</Button>
            <Button type="button" size="sm" onClick={addVideo} disabled={!draft.url} data-testid="chatter-video-add">Add video</Button>
          </div>
        </div>
      )}
    </div>
  );
}
